/* ═════════════════════════════════════════════════
   Kohdekonteksti — yläpalkin kohdevalinta (T426, Toni 7.10.2026 «4. OK kaikki» ja «Go»).

   Yksi valinta koko sovellukselle: «Kohde: … ▾» tai «Kaikki kohteet». Kohdesidonnaiset näkymät lukevat sen
   (`useKohde()`), eivät pidä omaa valintaansa. Valinta tallentuu palvelimelle (`PUT /api/kohdevalinta`, 204),
   joten se säilyy uloskirjautumisen ja toisen selaimen yli. Kenttätili jolla on yksi työmaa on lukittu siihen.

   Osoitteen `?kohde=` (esim. Hubin luvun linkki) voittaa: näkymä kutsuu `asetaOsoitteesta`, joka vaihtaa myös
   yläpalkin — yläpalkki ja ruutu eivät koskaan ole eri mieltä siitä mitä näytetään.

   Erehtyy kiinni päin: jos reitti ei vastaa (moduuli pois, tarkastajan tunnus), valitsin ei piirry ja näkymät
   toimivat «Kaikki kohteet» -tilassa omien rajaustensa varassa.
   ═════════════════════════════════════════════════ */
import { t } from "./kieli";
import { createContext, useCallback, useContext, useEffect, useRef, useState, type ReactNode } from "react";
import { api } from "./api";

export interface KohdeValintaKohde { id: number; tunnus: string; nimi: string; tila: string }
interface Konteksti {
  /** Valittu kohde tai null = «Kaikki kohteet». undefined = ei vielä ladattu. */
  valittu: number | null | undefined;
  lukittu: boolean;
  kohteet: KohdeValintaKohde[];
  kaytossa: boolean;
  /** `tallenna = false`: osoitteesta tullut kohde vaihtaa yläpalkin tälle istunnolle, mutta ei käyttäjän tallennettua valintaa. */
  aseta: (id: number | null, tallenna?: boolean) => Promise<void>;
  /** Kohdelista ja valinta uudelleen palvelimelta, kun kohteiden joukko muuttuu (arkistointi, palautus; testikortti 3221282
   *  kohdat 2 ja 4, Toni 4.11.2026 klo 11.55: valitsin päivittyi vasta F5:llä). */
  paivita: () => Promise<void>;
}

const Ktx = createContext<Konteksti>({ valittu: null, lukittu: false, kohteet: [], kaytossa: false, aseta: async () => {}, paivita: async () => {} });

export function KohdeKontekstiTarjoaja({ children }: { children: ReactNode }) {
  const [tila, setTila] = useState<Omit<Konteksti, "aseta" | "paivita">>({ valittu: undefined, lukittu: false, kohteet: [], kaytossa: false });
  const paivita = useCallback(async () => {
    await api.kohdevalinta()
      // Toni 15.10.2026 (T373 kohta 1, pekilon kävely 6112e35): tunnuksella täsmälleen yksi sallittu kohde → se on valittuna tälle
      // istunnolle tallentamatta. Ennen tätä yläpalkki näytti ainoan kohteen kiinteänä tekstinä, mutta valittu oli null, joten Hub
      // näytti kohdevalitsimen ja 3-viikkoissuunnitelma pyysi «Valitse kohde». Useamman kohteen tunnuksella ei muutosta.
      .then((r) => setTila({ valittu: r.valittu ?? (r.kohteet.length === 1 ? r.kohteet[0]!.id : null), lukittu: r.lukittu, kohteet: r.kohteet, kaytossa: true }))
      .catch(() => setTila({ valittu: null, lukittu: false, kohteet: [], kaytossa: false }));
  }, []);
  useEffect(() => { void paivita(); }, [paivita]);
  // 7.10.2026 (demon kävely b2f2948): osoitteen ?kohde= tallentui ensin pysyväksi valinnaksi, jolloin yksi linkki muutti
  // kaikki myöhemmin avatut näkymät (Laiteasennus tyhjä, Hubin valitsin piilossa) ja lukeva kävely olisi kirjoittanut.
  // Nyt vain yläpalkista itse tehty valinta tallentuu; linkin kohde koskee tätä istuntoa.
  const aseta = useCallback(async (id: number | null, tallenna = true) => {
    if (tila.lukittu) return;
    setTila((t) => ({ ...t, valittu: id }));
    if (!tallenna) return;
    try { const r = await api.asetaKohdevalinta(id); setTila((t) => ({ ...t, valittu: r.valittu })); }
    catch { /* palvelin kieltäytyi (vieras kohde): näkymä jää valintaan, seuraava lataus korjaa */ }
  }, [tila.lukittu]);
  return <Ktx.Provider value={{ ...tila, aseta, paivita }}>{children}</Ktx.Provider>;
}

export function useKohde(): Konteksti { return useContext(Ktx); }

/**
 * Näkymän oma kohdetila seuraa yläpalkkia (T426). Näkymä pitää `kohde`-tilansa (sitä käyttävät sen kyselyt ja
 * osoitesynkka), ja koukku:
 *  - yläpalkki → näkymä: valittu kohde asetetaan näkymälle; «Kaikki kohteet» tyhjentää vain jos näkymä sallii sen
 *    (`kaikkiSallittu`). Kohteen vaativa näkymä pitää silloin oman oletuksensa eikä työnnä arvaustaan yläpalkkiin.
 *  - osoite → yläpalkki: `osoitteenKohde` (esim. Hubin linkin `?kohde=`) voittaa ja vaihtaa yläpalkin tälle istunnolle
 *    (ei tallennu), myös ensimmäisellä latauksella, jotta yläpalkki ja ruutu eivät ole eri mieltä.
 *  - `valmis`: näkymä jonka osoitteen kohde ratkeaa vasta listan latauduttua (Hubin tunnus) odottaa sitä.
 * Kun yläpalkki ei ole käytössä (tarkastaja, moduuli pois), koukku ei tee mitään ja näkymän oma valitsin jää esiin.
 */
export function useKohdeSynkka(kohde: number | "", setKohde: (v: number | "") => void,
  { osoitteenKohde, kaikkiSallittu, valmis = true }: { osoitteenKohde?: number | ""; kaikkiSallittu: boolean; valmis?: boolean }) {
  const { valittu, kaytossa, aseta } = useKohde();
  const ladattu = useRef(false);
  // Tila eikä vain ref: kun yläpalkin valinta jo vastaa näkymää, setKohde ei aiheuta uutta piirtoa, ja `synkattu` jäisi epätodeksi.
  const [ladattuTila, setLadattuTila] = useState(false);
  useEffect(() => {
    if (!kaytossa || valittu === undefined || !valmis) return;
    if (!ladattu.current) {
      ladattu.current = true;
      setLadattuTila(true);
      if (typeof osoitteenKohde === "number" && osoitteenKohde !== valittu) { void aseta(osoitteenKohde, false); return; }
    }
    if (valittu !== null) { if (kohde !== valittu) setKohde(valittu); }
    else if (kaikkiSallittu && kohde !== "") setKohde("");
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [valittu, kaytossa, valmis]);
  useEffect(() => {
    if (!kaytossa || valittu === undefined || !valmis || !ladattu.current) return;
    if (typeof osoitteenKohde === "number" && osoitteenKohde !== valittu) void aseta(osoitteenKohde, false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [osoitteenKohde]);
  /* T472/T473 (videon 3 havainnot 10.10.2026): näkymä haki ensin «kaikki kohteet» (kohde = "") ja vasta yläpalkin
     valinnan saavuttua oikean kohteen — Etenemä näytti ensimmäisellä latauksella 5 868 dia-inch kaikista kohteista ja
     uudelleenlatauksella 4 244 (N-2410). `synkattu` kertoo, että näkymän kohde vastaa yläpalkkia: ennen sitä ei haeta. */
  const synkattu = !kaytossa || (valittu !== undefined && ladattuTila
    && (valittu !== null ? kohde === valittu : (!kaikkiSallittu || kohde === "")));
  return { ylapalkissa: kaytossa, ylaValittu: valittu ?? null, synkattu };
}

/** Yläpalkin valitsin. Lukittu kenttätili ja tunnus jolla on vain yksi kohde näkevät kohteen tekstinä, ei valintaa
 *  (pääChatin huomio Tonin välittämänä 7.10.2026). `lyhyt`: puhelimen yläpalkin rivi — vain kohteen tunnus, aina näkyvissä,
 *  jotta kenttätyöntekijä näkee missä kohteessa on, vaikka valitsin on valikossa. */
export function YlapalkinKohdevalinta({ lyhyt = false }: { lyhyt?: boolean }) {
  const { valittu, lukittu, kohteet, kaytossa, aseta } = useKohde();
  if (!kaytossa || valittu === undefined) return null;
  const nimi = (k: KohdeValintaKohde) => `${k.tunnus} · ${k.nimi}`;
  const ainoa = kohteet.length === 1 ? kohteet[0]! : null;
  if (lyhyt) {
    const k = kohteet.find((x) => x.id === valittu) ?? ainoa;
    return <span className="palkki__kohde palkki__kohde--lyhyt" data-ylakohde-lyhyt={k?.id ?? ""} title={k ? nimi(k) : t("kohde.kaikki")}>
      {k ? `${t("kohde.otsikko")} ${k.tunnus}` : t("kohde.kaikki")}</span>;
  }
  if (ainoa && !lukittu) {
    return <span className="palkki__kohde" data-ylakohde={ainoa.id} data-ylakohde-ainoa="">{t("kohde.otsikko")}: {nimi(ainoa)}</span>;
  }
  if (lukittu) {
    const k = kohteet.find((x) => x.id === valittu);
    return <span className="palkki__kohde" data-ylakohde={valittu ?? ""} data-ylakohde-lukittu="">{t("kohde.otsikko")}: {k ? nimi(k) : "—"}</span>;
  }
  return (
    <label className="palkki__kohde" data-ylakohde={valittu ?? ""}>
      <span className="palkki__kohde-otsikko">{t("kohde.otsikko")}:</span>
      <select value={valittu ?? ""} aria-label={t("kohde.otsikko")} data-ylakohde-valinta=""
              onChange={(e) => void aseta(e.target.value ? Number(e.target.value) : null)}>
        <option value="">{t("kohde.kaikki")}</option>
        {kohteet.map((k) => <option key={k.id} value={k.id}>{nimi(k)}</option>)}
      </select>
    </label>
  );
}

/** Kohteiden yli menevän näkymän merkintä: suodatin ei koske tätä näkymää (T426). */
export function EiKohdesuodatinta() {
  const { valittu, kaytossa } = useKohde();
  if (!kaytossa || !valittu) return null;
  return <p className="dim" data-ei-kohdesuodatinta="" style={{ margin: "0 0 8px" }}>{t("kohde.ei_suodata")}</p>;
}
